import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";

interface sensorItem {
  id: string;
  name: string;
  aqi: number;
  showDialog: boolean;
  chartWidth: number;
  chartHeight: number;
  setLastUpdate: (timestamp: string) => void; 
}

const SideAirQualityChart: React.FC<sensorItem> = ({
  id,
  name,
  aqi,
  showDialog,
  chartWidth,
  chartHeight,
  setLastUpdate
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>();
  const [initialData, setInitialData] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const AIR_QUALITY_THREE_DAYS_URL =
  process.env.REACT_APP_AIR_QUALITY_THREE_DAYS_URL

  useEffect(() => {
    const fetchDataAndInitializeChart = async () => {



      try {
        const response = await fetch(
            `${AIR_QUALITY_THREE_DAYS_URL}` +
            id
        );
        const responseData = await response.json();

        const { maxTimestamp } = responseData;
          setLastUpdate(maxTimestamp);

        if (responseData && Array.isArray(responseData.results)) {
          const data = responseData.results.map((item: any) => [
            formatTimestamp(item.timestamp),
            item.value,
          ]);
       
          setInitialData(data);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchDataAndInitializeChart();
  }, [id]);

  const setAqiColor = (aqiValue: number): string => {
    if (aqiValue !== null) {
      if (0 < aqiValue && aqiValue <= 50) {
        return "#03FE03";
      } else if (50 < aqiValue && aqiValue <= 100) {
        return "#FFFF33";
      } else if (100 < aqiValue && aqiValue <= 150) {
        return "#D4A017";
      } else if (150 < aqiValue && aqiValue <= 200) {
        return "#F1594C";
      } else if (200 < aqiValue && aqiValue <= 300) {
        return "#6C2DC7";
      } else if (300 < aqiValue && aqiValue <= 500) {
        return "#7B1515";
      }
    }
    return "#03FE03";
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {

    if(chartWidth !== 0 || chartHeight !== 0){
      setInitialLoad(false);
    }
  }, [chartWidth,initialLoad,chartHeight]);

  useEffect(() => {
    if (
      chartRef.current &&
      chartRef.current.clientWidth &&
      chartRef.current.clientHeight
    ) {
      const chart = echarts.init(chartRef.current);

      const chartOption: EChartsOption = {
    
        color: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],

        title: {
          left: "center",
          text: name,
          textStyle: {
            fontSize:"0.8rem",
            color: setAqiColor(aqi),
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: [name],
          textStyle: {
            color: "#FFFFFF",
          },
          right: "10%",
          top: "10%",
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: "none",
            },
            restore: {},
            saveAsImage: {},
          },
        },

        grid: {
          left: "3%",
          right: "14%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          name: "Time",
          axisLabel: {
            color: "#FFFFFF",
            formatter: function (value: string) {
              return value.replace("T", " ").replace(".000Z", "");
            },
          },
          //   boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#FFFFFF",
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "AQI Index",
          nameTextStyle: {
            color: "#FFFFFF",
          },
          axisLabel: {
            formatter: "{value}",
            color: "#FFFFFF",
          },
          boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#070807",
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              color: "rgba(85, 84, 95, 0.6)",
              width: 1,
            },
          },
          // ...splitLineStyle,
        },

        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
          },
        ],
        visualMap: {
          type: "piecewise",
          show: false,
          dimension: 1,
          pieces: [
            {
              gt: 0,
              lt: 50,
              color: "#03FE03",
            },
            {
              gt: 50,
              lt: 100,
              color: "#FFFF33",
            },
            {
              gt: 100,
              lt: 150,
              color: "#D4A017",
            },
            {
              gt: 150,
              lt: 200,
              color: "#F1594C",
            },
            {
              gt: 200,
              lt: 300,
              color: "#6C2DC7",
            },
            {
              gt: 300,
              lt: 500,
              color: "#7B1515",
            },
          ],
        },
        series: [
          {
            name: name,
            type: "line",
            stack: "Total",
            smooth: true,
            symbol: "none",
            lineStyle: {
              width: 1,
            },
            areaStyle: {
              opacity: 0.8,
            },
            emphasis: {
              focus: "series",
            },
            data: initialData,
            markLine: {
              symbol: "circle",
              data: [
                {
                  name: "Hazardous",
                  yAxis: 500,
                  itemStyle: {
                    color: "#7B1515", 
                  },
                  label: {
                    formatter: " Hazardous",
                  },
                },
                {
                  name: "Very Unhealthy",
                  yAxis: 300,
                  itemStyle: {
                    color: "#6C2DC7",
                  },
                  label: {
                    formatter: " Very Unhealthy",
                  },
                },
                {
                  name: "Unhealthy",
                  yAxis: 200,
                  itemStyle: {
                    color: "#F1594C",
                  },
                  label: {
                    formatter: " Unhealthy",
                  },
                },
                {
                  name: "Unhealthy for sensitive group",
                  yAxis: 150,
                  itemStyle: {
                    color: "#D4A017",
                  },
                  label: {
                    formatter: " Unhealthy for sensitive group",
                  },
                },
                {
                  name: "Moderate",
                  yAxis: 100,
                  itemStyle: {
                    color: "#FFFF33",
                  },
                  label: {
                    formatter: " Moderate",
                  },
                },
                {
                  name: "Good",
                  yAxis: 50,
                  itemStyle: {
                    color: "#03FE03",
                  },
                  label: {
                    formatter: " Good",
                  },
                },
              ],
            },
          },
        ],
      };

      chart.setOption(chartOption);
      setChartInstance(chart);
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, [initialData]);

  const generateNewData = async () => {
    try {
      const response = await fetch(
        `${AIR_QUALITY_THREE_DAYS_URL}` +
          id
      );
      const responseData = await response.json();

      if (responseData && Array.isArray(responseData.results)) {
        const newData = responseData.results.map((item: any) => [
          item.timestamp,
          item.value,
        ]);
        setInitialData((prevData) => [...prevData, ...newData]);
      } else {
        console.error("Invalid response data format:", responseData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (showDialog) {
      const interval = setInterval(() => {
        generateNewData();
      }, 15 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [showDialog]);


  useEffect(() => {
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [chartWidth,chartHeight]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  return (
    <div
      id="main"
      style={{
        width: initialLoad ? "100%" : `${chartWidth}px`,
        height: initialLoad?"45vh" :`${chartHeight}px`}} 
      ref={chartRef}
    ></div>
  );
};

export default SideAirQualityChart;
